import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  computeCourierMeasures,
  canMarkCourierShipped,
  shippedRequiredMissing,
  isCourierPiecesLocked,
  isSpainCourierEnvio,
} from "../../utils/courierControlPure";
import {
  COURIER_COMPANIES,
  COURIER_MOTIVO_OPTIONS,
  COURIER_PACKING_OPTIONS,
  STAGE_CHINA_ORIGIN,
  buildOriginOptionsForUser,
  courierPackingI18nKey,
  destinationTiposForCourierUser,
  destinoLabelFromEnvioDestino,
  filterRequestedByUsers,
  inferCountryFromOrigin,
  inferEnvioDestinoFromCourier,
  isChinaCourierUser,
  isStageChinaUser,
  userDisplayName,
} from "../../utils/courierControlSelects";
import {
  searchMuestrasCourier,
  getCourierMuestraLineaFields,
} from "../../api/CourierControl/courierControlApi";
import {
  isCourierSampleMotivo,
  shouldShowSampleDetails,
  sampleDetailsChanged,
  normalizeCourierMotivoValue,
} from "../../utils/courierSamplePiece";
import { getProveedores } from "../../api/ProveedoresyFabricas/proveedoresApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { getAllUsers } from "../../api/User/userApi";
import EnvioDestinoFields, {
  emptyEnvioDestino,
} from "../Muestras/EnvioDestinoFields";
import { pageFieldSx } from "../../styles/pageModernStyles";

export const emptyLine = (partial = {}) => ({
  Temporada: "",
  Marca: "",
  Estilo: "",
  TipoPrenda: "",
  PesoKg: "",
  Tejido: "",
  Color: "",
  Talla: "",
  TipoMuestra: "",
  CodigoMuestra: "",
  IdMuestra: null,
  IdMovimientoMuestra: null,
  Motivo: "sample",
  ...partial,
});

export const emptyEnvio = (partial = {}) => ({
  Direccion: "SALIDA",
  Ambito: "NACIONAL",
  Fecha: new Date().toISOString().slice(0, 10),
  SolicitadoPor: "",
  Origen: "",
  Destino: "",
  Contacto: "",
  TipoEmbalaje: "",
  LargoCm: "",
  AnchoCm: "",
  AltoCm: "",
  PesoBrutoKg: "",
  CompaniaCourier: "",
  NumeroAwb: "",
  Coste: "",
  CosteReal: "",
  Pais: "",
  Estado: "PEDIDO",
  Notas: "",
  lineas: [emptyLine()],
  ...partial,
});

const fmt = (value, digits = 3) => {
  if (value == null || value === "") return "—";
  return Number(value).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

/** Pedido azul · Preparando aduanas índigo · Enviado naranja · Entregado verde · Cancelado rojo */
const statusAccent = (theme, estado) => {
  const e = String(estado || "PEDIDO").toUpperCase();
  if (e === "PREPARANDO_ADUANAS")
    return theme.palette.info.dark || theme.palette.secondary.main;
  if (e === "EN_CURSO") return theme.palette.warning.main;
  if (e === "ENTREGADO") return theme.palette.success.main;
  if (e === "CANCELADO") return theme.palette.error.main;
  return theme.palette.info.main; // PEDIDO
};

const statusColor = (estado) => {
  const e = String(estado || "").toUpperCase();
  if (e === "ENTREGADO") return "success";
  if (e === "PREPARANDO_ADUANAS") return "secondary";
  if (e === "EN_CURSO") return "warning";
  if (e === "CANCELADO") return "error";
  return "info"; // PEDIDO
};

const FormPanel = ({ icon, title, subtitle, accent, children, className }) => {
  const theme = useTheme();
  const color = accent || theme.palette.primary.main;
  return (
    <Box
      className={className}
      sx={{
        mb: 2,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(color, 0.18),
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 6px 20px rgba(0,0,0,0.28)"
            : `0 8px 24px ${alpha(color, 0.07)}`,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
        sx={{
          px: 2,
          py: 1.4,
          borderBottom: "1px solid",
          borderColor: alpha(color, 0.12),
          background: `linear-gradient(120deg, ${alpha(color, 0.1)} 0%, ${alpha(
            theme.palette.background.paper,
            0.2,
          )} 70%)`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.75,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(color, 0.14),
            color,
            flexShrink: 0,
            mt: 0.1,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, pt: 0.15 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.78rem",
              letterSpacing: 0.7,
              textTransform: "uppercase",
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Box sx={{ px: 2, py: 1.75 }}>{children}</Box>
    </Box>
  );
};

function SampleCodeField({ value, onChange, fieldSx, disabled = false }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const q = String(value.CodigoMuestra || "").trim();
    if (q.length < 2 || disabled) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = await searchMuestrasCourier(q);
        if (!cancelled) setOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value.CodigoMuestra, disabled]);

  return (
    <Autocomplete
      freeSolo
      size="small"
      disabled={disabled}
      options={options}
      filterOptions={(opts) => opts}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.MUESTRA || ""
      }
      inputValue={value.CodigoMuestra || ""}
      onInputChange={(_, next, reason) => {
        if (disabled || reason === "reset") return;
        onChange({
          ...value,
          CodigoMuestra: next,
          IdMuestra: null,
        });
      }}
      onChange={(_, next) => {
        if (disabled) return;
        if (next && typeof next === "object") {
          onChange({
            ...value,
            CodigoMuestra: next.MUESTRA || "",
            IdMuestra: next.ID_MUESTRA,
          });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("courierControl.sampleCode")}
          sx={fieldSx}
          helperText={
            value.IdMovimientoMuestra
              ? t("courierControl.linkedFromShipment")
              : value.IdMuestra
                ? t("courierControl.linkedSampleOnly")
                : t("courierControl.sampleHint")
          }
        />
      )}
    />
  );
}

function PieceCard({
  linea,
  index,
  onChange,
  onRemove,
  canRemove,
  requestDisabled,
  pieceFieldsDisabled,
  fieldSx,
  t,
}) {
  const theme = useTheme();
  const fieldsLocked = Boolean(pieceFieldsDisabled || requestDisabled);
  const hasSamp = Boolean(
    String(linea.CodigoMuestra || "").trim() ||
    linea.IdMuestra ||
    linea.IdMovimientoMuestra,
  );
  const hasContent = Boolean(String(linea.Estilo || "").trim());
  const showSamp = hasSamp || !hasContent;
  const showContent = hasContent || !hasSamp;
  const showSampleDetails = shouldShowSampleDetails(linea);
  const title =
    String(linea.CodigoMuestra || "").trim() ||
    String(linea.Estilo || "").trim() ||
    `${t("courierControl.piece")} ${index + 1}`;
  const [openDetails, setOpenDetails] = useState(showSampleDetails);
  const motivoValue = normalizeCourierMotivoValue(linea.Motivo);
  const motivoKnown = COURIER_MOTIVO_OPTIONS.includes(motivoValue);
  const motivoRaw = String(linea.Motivo || "").trim();

  useEffect(() => {
    setOpenDetails(showSampleDetails);
  }, [showSampleDetails]);

  // Normaliza Motivo legado Matriz → sample
  useEffect(() => {
    if (!motivoRaw || motivoRaw === motivoValue) return;
    if (isCourierSampleMotivo(motivoRaw) && motivoValue === "sample") {
      onChange({ ...linea, Motivo: "sample" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motivoRaw, motivoValue]);

  useEffect(() => {
    if (!showSampleDetails) return undefined;
    let cancelled = false;

    const applyFields = (fields) => {
      if (cancelled || !fields) return;
      const merged = {
        ...linea,
        ...fields,
        Motivo: "sample",
        IdMovimientoMuestra: linea.IdMovimientoMuestra,
      };
      if (sampleDetailsChanged(linea, merged)) onChange(merged);
    };

    const run = async () => {
      let id = Number(linea.IdMuestra);
      if (!Number.isFinite(id) || id <= 0) {
        const code = String(linea.CodigoMuestra || "").trim();
        if (!code) return;
        const rows = await searchMuestrasCourier(code);
        const hit =
          (Array.isArray(rows) ? rows : []).find(
            (r) =>
              String(r.MUESTRA || "")
                .trim()
                .toLowerCase() === code.toLowerCase(),
          ) || (Array.isArray(rows) ? rows[0] : null);
        id = Number(hit?.ID_MUESTRA);
      }
      if (!Number.isFinite(id) || id <= 0) return;
      const fields = await getCourierMuestraLineaFields(id);
      applyFields(fields);
    };

    run().catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSampleDetails, linea.IdMuestra, linea.CodigoMuestra]);

  const clearSampleDetails = (base) => ({
    ...base,
    Temporada: "",
    Marca: "",
    TipoPrenda: "",
    TipoMuestra: "",
    Tejido: "",
    Color: "",
    Talla: "",
  });

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.85),
        borderRadius: 2,
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        overflow: "hidden",
        mb: 1.25,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.55),
        }}
      >
        <Typography
          sx={{ fontWeight: 800, fontSize: "0.9rem", flex: 1 }}
          noWrap
        >
          {title}
        </Typography>
        {linea.IdMovimientoMuestra ? (
          <Chip
            size="small"
            label={t("courierControl.fromMatrixShipment")}
            sx={{ height: 24, fontWeight: 700 }}
            color="primary"
            variant="outlined"
          />
        ) : linea.IdMuestra ? (
          <Chip
            size="small"
            label={t("courierControl.sampleLinkOnly")}
            sx={{ height: 24, fontWeight: 700 }}
            color="warning"
            variant="outlined"
          />
        ) : null}
        {showSampleDetails ? (
          <IconButton
            size="small"
            onClick={() => setOpenDetails((v) => !v)}
            aria-label={t("courierControl.pieceDetails")}
          >
            {openDetails ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        ) : null}
        {canRemove ? (
          <IconButton
            size="small"
            aria-label={t("courierControl.deleteLine")}
            onClick={onRemove}
            disabled={fieldsLocked}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      <Box sx={{ p: 1.5 }}>
        <Grid container spacing={1.25}>
          {showSamp ? (
            <Grid item xs={12} sm={showContent ? 4 : 5}>
              <SampleCodeField
                value={linea}
                fieldSx={fieldSx}
                disabled={fieldsLocked || Boolean(linea.IdMovimientoMuestra)}
                onChange={(next) => {
                  const code = String(next.CodigoMuestra || "").trim();
                  onChange({
                    ...next,
                    Estilo: code || next.IdMuestra ? "" : next.Estilo || "",
                    Motivo:
                      code || next.IdMuestra
                        ? isCourierSampleMotivo(next.Motivo) || !next.Motivo
                          ? "sample"
                          : next.Motivo
                        : next.Motivo,
                  });
                }}
              />
            </Grid>
          ) : null}
          {showContent ? (
            <Grid item xs={12} sm={showSamp ? 4 : 5}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.style")}
                value={linea.Estilo || ""}
                disabled={fieldsLocked}
                sx={fieldSx}
                onChange={(e) => {
                  const estilo = e.target.value;
                  onChange({
                    ...linea,
                    Estilo: estilo,
                    ...(String(estilo || "").trim()
                      ? {
                          CodigoMuestra: "",
                          IdMuestra: null,
                        }
                      : null),
                  });
                }}
              />
            </Grid>
          ) : null}
          <Grid item xs={12} sm={showSamp && showContent ? 4 : 7}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("courierControl.reason")}
              value={motivoKnown ? motivoValue : motivoRaw || ""}
              disabled={fieldsLocked}
              sx={fieldSx}
              onChange={(e) => {
                const nextMotivo = e.target.value;
                const next = { ...linea, Motivo: nextMotivo };
                if (["swatch", "color", "card", "other"].includes(nextMotivo)) {
                  onChange(clearSampleDetails(next));
                } else {
                  onChange(next);
                }
              }}
            >
              {motivoRaw &&
              !motivoKnown &&
              !isCourierSampleMotivo(motivoRaw) ? (
                <MenuItem value={motivoRaw}>{motivoRaw}</MenuItem>
              ) : null}
              {COURIER_MOTIVO_OPTIONS.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(
                    `courierControl.reason${key.charAt(0).toUpperCase()}${key.slice(1)}`,
                  )}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {showSampleDetails ? (
          <Collapse in={openDetails}>
            <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.season")}
                  value={linea.Temporada || ""}
                  disabled
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.brand")}
                  value={linea.Marca || ""}
                  disabled
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.garmentType")}
                  value={linea.TipoPrenda || ""}
                  disabled
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.sampleType")}
                  value={linea.TipoMuestra || ""}
                  disabled
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.fabric")}
                  value={linea.Tejido || ""}
                  disabled
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.color")}
                  value={linea.Color || ""}
                  disabled
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.size")}
                  value={linea.Talla || ""}
                  disabled
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Collapse>
        ) : null}
      </Box>
    </Box>
  );
}

export default function CourierControlDialog({
  open,
  envio,
  saving,
  onClose,
  onChange,
  onSave,
  onDelete,
  canSolicitar = false,
  canTerminar = false,
  currentUser = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fieldSx = pageFieldSx(theme);
  // Pedido: editable. Enviado / Preparando aduanas / Entregado / Cancelado: todo bloqueado.
  // Terminar solo puede cambiar el Estado (p. ej. volver a Pedido para editar).
  const estadoUpper = String(envio.Estado || "PEDIDO").toUpperCase();
  const piecesLocked = isCourierPiecesLocked(estadoUpper);
  const formLocked = piecesLocked;
  const shippedLocked = formLocked;
  const hasEditPerm = canSolicitar || canTerminar;
  const requestDisabled = !hasEditPerm || formLocked;
  const finishDisabled = !hasEditPerm || formLocked;
  const statusDisabled = canTerminar ? false : !canSolicitar || formLocked;
  const addPieceDisabled = !hasEditPerm || piecesLocked;
  const canSave = canTerminar || (canSolicitar && !formLocked);
  const canDelete = Boolean(envio.IdCourierControl) && canTerminar;
  const showPreparandoAduanas =
    canTerminar &&
    (isSpainCourierEnvio(envio) || estadoUpper === "PREPARANDO_ADUANAS");
  const isChinaUser = isChinaCourierUser(currentUser || {});
  const isStageUser = isStageChinaUser(currentUser || {});
  const originLocked = isStageUser;
  const [pendingRemove, setPendingRemove] = useState(null); // { index, label, linked }
  const [confirmShipOpen, setConfirmShipOpen] = useState(false);
  const [shipAck, setShipAck] = useState(false);
  const [shipMissing, setShipMissing] = useState([]);
  const [confirmDeliveredOpen, setConfirmDeliveredOpen] = useState(false);
  const [deliverAck, setDeliverAck] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [destinoForm, setDestinoForm] = useState(() => emptyEnvioDestino());

  const deliveredSampleLines = useMemo(() => {
    const lineas = Array.isArray(envio?.lineas) ? envio.lineas : [];
    return lineas.filter(
      (l) =>
        Number(l.IdMuestra) > 0 ||
        String(l.CodigoMuestra || "").trim() ||
        String(l.Estilo || "").trim(),
    );
  }, [envio?.lineas]);

  const measures = useMemo(
    () =>
      computeCourierMeasures({
        largoCm: envio.LargoCm,
        anchoCm: envio.AnchoCm,
        altoCm: envio.AltoCm,
        pesoBrutoKg: envio.PesoBrutoKg,
        coste: envio.Coste,
        costeReal: envio.CosteReal,
      }),
    [envio],
  );

  const originOptions = useMemo(
    () => buildOriginOptionsForUser(proveedores, currentUser || {}),
    [proveedores, currentUser],
  );

  const allowedDestinoTipos = useMemo(
    () => destinationTiposForCourierUser(currentUser || {}),
    [currentUser],
  );

  const requestedByOptions = useMemo(
    () => filterRequestedByUsers(users, currentUser || {}, proveedores),
    [users, currentUser, proveedores],
  );

  const userNameOptions = useMemo(() => {
    const names = users.map(userDisplayName).filter(Boolean);
    const current = String(envio.SolicitadoPor || "").trim();
    if (current && !names.includes(current)) names.unshift(current);
    return names;
  }, [users, envio.SolicitadoPor]);

  const courierCompanyOptions = useMemo(() => {
    const current = String(envio.CompaniaCourier || "").trim();
    if (current && !COURIER_COMPANIES.includes(current)) {
      return [current, ...COURIER_COMPANIES];
    }
    return COURIER_COMPANIES;
  }, [envio.CompaniaCourier]);

  useEffect(() => {
    if (!open) {
      setConfirmShipOpen(false);
      setShipAck(false);
      setShipMissing([]);
      setConfirmDeliveredOpen(false);
      setDeliverAck(false);
      setPendingRemove(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.all([
      getProveedores().catch(() => []),
      getCustomers().catch(() => []),
      getAllUsers().catch(() => []),
    ]).then(([prov, cust, usr]) => {
      if (cancelled) return;
      setProveedores(Array.isArray(prov) ? prov : []);
      setCustomers(Array.isArray(cust) ? cust : []);
      setUsers(Array.isArray(usr) ? usr : []);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDestinoForm(
      inferEnvioDestinoFromCourier(envio, { customers, proveedores }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, envio.IdCourierControl, customers, proveedores]);

  // Stage China: origen fijo + país; Solicitado por = usuario actual si vacío
  useEffect(() => {
    if (!open || !currentUser) return;
    const patch = {};
    if (isStageUser) {
      if (envio.Origen !== STAGE_CHINA_ORIGIN) {
        patch.Origen = STAGE_CHINA_ORIGIN;
      }
      if (!envio.Pais || /china/i.test(String(envio.Pais))) {
        patch.Pais = "China";
      }
    }
    if (isChinaUser && !String(envio.SolicitadoPor || "").trim()) {
      patch.SolicitadoPor = userDisplayName(currentUser);
    }
    if (Object.keys(patch).length) {
      onChange({ ...envio, ...patch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isStageUser, isChinaUser, currentUser?.id]);

  const setField = (field, value) => onChange({ ...envio, [field]: value });

  const requestMarkShipped = () => {
    const missing = shippedRequiredMissing(envio);
    if (missing.length) {
      setShipMissing(missing);
      setConfirmShipOpen(false);
      setShipAck(false);
      return;
    }
    setShipMissing([]);
    setShipAck(false);
    setConfirmShipOpen(true);
  };

  const handleEstadoChange = (nextEstado) => {
    if (
      nextEstado === "EN_CURSO" &&
      estadoUpper !== "EN_CURSO" &&
      !canTerminar
    ) {
      requestMarkShipped();
      return;
    }
    if (nextEstado === "ENTREGADO" && estadoUpper !== "ENTREGADO") {
      setDeliverAck(false);
      setConfirmDeliveredOpen(true);
      return;
    }
    setShipMissing([]);
    setField("Estado", nextEstado);
  };

  const confirmMarkShipped = () => {
    if (!canMarkCourierShipped(envio) || !shipAck) return;
    setConfirmShipOpen(false);
    setShipAck(false);
    setShipMissing([]);
    // Guarda ya como Enviado (draft sigue Pedido → confirmingShip). Al reabrir: bloqueado.
    if (typeof onSave === "function") {
      onSave({ ...envio, Estado: "EN_CURSO" });
    }
  };

  const confirmMarkDelivered = () => {
    if (!deliverAck) return;
    setConfirmDeliveredOpen(false);
    setDeliverAck(false);
    if (typeof onSave === "function") {
      onSave({ ...envio, Estado: "ENTREGADO" });
    } else {
      setField("Estado", "ENTREGADO");
    }
  };

  const applyOrigin = (origen) => {
    if (originLocked) return;
    const nextPais =
      inferCountryFromOrigin(origen, proveedores) || envio.Pais || "";
    onChange({ ...envio, Origen: origen, Pais: nextPais });
  };

  const applyDestinoForm = (nextDest) => {
    setDestinoForm(nextDest);
    const destinoLabel = destinoLabelFromEnvioDestino(nextDest, {
      customers,
      proveedores,
    });
    onChange({
      ...envio,
      Destino: destinoLabel || envio.Destino || "",
      Contacto: nextDest.PERSONA_ENVIO || "",
    });
  };

  const setLine = (index, next) => {
    const lineas = envio.lineas.map((linea, i) => (i === index ? next : linea));
    onChange({ ...envio, lineas });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.75),
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 18px 48px rgba(0,0,0,0.55)"
                : "0 20px 50px rgba(15,23,42,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 2.5,
            borderBottom: "1px solid",
            borderColor: alpha(statusAccent(theme, envio.Estado), 0.35),
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(statusAccent(theme, envio.Estado), 0.32)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`
                : `linear-gradient(135deg, ${alpha(statusAccent(theme, envio.Estado), 0.18)} 0%, ${alpha(statusAccent(theme, envio.Estado), 0.06)} 48%, ${theme.palette.background.paper} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.25,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(statusAccent(theme, envio.Estado), 0.16),
                  color: statusAccent(theme, envio.Estado),
                }}
              >
                <LocalShippingOutlinedIcon />
              </Box>
              <Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.2 }}
                >
                  {envio.IdCourierControl
                    ? t("courierControl.editTitle")
                    : t("courierControl.newRequest")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.35, fontWeight: 500, maxWidth: 480 }}
                >
                  {t("courierControl.dialogSubtitle")}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={
                  envio.Direccion === "ENTRADA"
                    ? t("courierControl.entry")
                    : t("courierControl.exit")
                }
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={
                  envio.Ambito === "INTERNACIONAL"
                    ? t("courierControl.international")
                    : t("courierControl.national")
                }
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                color={statusColor(envio.Estado)}
                label={
                  envio.Estado === "ENTREGADO"
                    ? t("courierControl.statusEntregado")
                    : envio.Estado === "EN_CURSO"
                      ? t("courierControl.statusEnCurso")
                      : envio.Estado === "PREPARANDO_ADUANAS"
                        ? t("courierControl.statusPreparandoAduanas")
                        : envio.Estado === "CANCELADO"
                          ? t("courierControl.statusCancelado")
                          : t("courierControl.statusPedido")
                }
                sx={{ fontWeight: 800 }}
              />
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 1.5, sm: 2.25 },
            py: 2,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.default, 0.45)
                : alpha(theme.palette.grey[50], 0.85),
            ...(requestDisabled
              ? {
                  "& .cc-request .MuiInputBase-root": {
                    pointerEvents: "none",
                    opacity: 0.72,
                  },
                  // Estado lo controla statusDisabled (Terminar puede volver a Pedido)
                  "& .cc-request .cc-status .MuiInputBase-root": {
                    pointerEvents: statusDisabled ? "none" : "auto",
                    opacity: statusDisabled ? 0.72 : 1,
                  },
                  "& .cc-pieces": {
                    pointerEvents: "none",
                    opacity: 0.85,
                  },
                }
              : null),
            ...(finishDisabled
              ? {
                  "& .cc-finish .MuiInputBase-root": {
                    pointerEvents: "none",
                    opacity: 0.72,
                  },
                }
              : null),
            ...(statusDisabled
              ? {
                  "& .cc-status .MuiInputBase-root": {
                    pointerEvents: "none",
                    opacity: 0.72,
                  },
                }
              : null),
          }}
        >
          <FormPanel
            className="cc-request"
            icon={<RouteOutlinedIcon fontSize="small" />}
            title={t("courierControl.sectionRoute")}
            subtitle={t("courierControl.sectionRouteHint")}
            accent={theme.palette.primary.main}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.direction")}
                  value={envio.Direccion}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("Direccion", e.target.value)}
                >
                  <MenuItem value="SALIDA">{t("courierControl.exit")}</MenuItem>
                  <MenuItem value="ENTRADA">
                    {t("courierControl.entry")}
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.scope")}
                  value={envio.Ambito}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("Ambito", e.target.value)}
                >
                  <MenuItem value="NACIONAL">
                    {t("courierControl.national")}
                  </MenuItem>
                  <MenuItem value="INTERNACIONAL">
                    {t("courierControl.international")}
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("courierControl.date")}
                  InputLabelProps={{ shrink: true }}
                  value={String(envio.Fecha || "").slice(0, 10)}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("Fecha", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={3} className="cc-status">
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.statusShipment")}
                  value={envio.Estado || "PEDIDO"}
                  disabled={statusDisabled}
                  sx={fieldSx}
                  onChange={(e) => handleEstadoChange(e.target.value)}
                >
                  {canTerminar || estadoUpper === "PEDIDO" ? (
                    <MenuItem value="PEDIDO">
                      {t("courierControl.statusPedido")}
                    </MenuItem>
                  ) : null}
                  {showPreparandoAduanas ||
                  estadoUpper === "PREPARANDO_ADUANAS" ? (
                    <MenuItem
                      value="PREPARANDO_ADUANAS"
                      disabled={!canTerminar}
                    >
                      {t("courierControl.statusPreparandoAduanas")}
                    </MenuItem>
                  ) : null}
                  <MenuItem value="EN_CURSO">
                    {t("courierControl.statusEnCurso")}
                  </MenuItem>
                  {canTerminar || estadoUpper === "ENTREGADO" ? (
                    <MenuItem value="ENTREGADO" disabled={!canTerminar}>
                      {t("courierControl.statusEntregado")}
                    </MenuItem>
                  ) : null}
                  {canTerminar || estadoUpper === "CANCELADO" ? (
                    <MenuItem value="CANCELADO" disabled={!canTerminar}>
                      {t("courierControl.statusCancelado")}
                    </MenuItem>
                  ) : null}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                {originLocked ? (
                  <TextField
                    fullWidth
                    size="small"
                    label={t("courierControl.origin")}
                    value={STAGE_CHINA_ORIGIN}
                    disabled
                    sx={fieldSx}
                  />
                ) : (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("courierControl.origin")}
                    value={
                      originOptions.includes(envio.Origen)
                        ? envio.Origen
                        : envio.Origen || ""
                    }
                    disabled={requestDisabled}
                    sx={fieldSx}
                    onChange={(e) => applyOrigin(e.target.value)}
                  >
                    {!envio.Origen ||
                    originOptions.includes(envio.Origen) ? null : (
                      <MenuItem value={envio.Origen}>{envio.Origen}</MenuItem>
                    )}
                    {originOptions.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.country")}
                  value={envio.Pais || ""}
                  disabled={requestDisabled || originLocked}
                  sx={fieldSx}
                  onChange={(e) => setField("Pais", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                {isChinaUser ? (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("courierControl.requestedBy")}
                    value={envio.SolicitadoPor || ""}
                    disabled={requestDisabled}
                    sx={fieldSx}
                    onChange={(e) => setField("SolicitadoPor", e.target.value)}
                  >
                    {envio.SolicitadoPor &&
                    !requestedByOptions.some(
                      (u) => userDisplayName(u) === envio.SolicitadoPor,
                    ) ? (
                      <MenuItem value={envio.SolicitadoPor}>
                        {envio.SolicitadoPor}
                      </MenuItem>
                    ) : null}
                    {requestedByOptions.map((u) => {
                      const label = userDisplayName(u);
                      return (
                        <MenuItem key={u.id || label} value={label}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                ) : (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("courierControl.requestedBy")}
                    value={envio.SolicitadoPor || ""}
                    disabled={requestDisabled}
                    sx={fieldSx}
                    onChange={(e) => setField("SolicitadoPor", e.target.value)}
                  >
                    {envio.SolicitadoPor &&
                    !userNameOptions.includes(envio.SolicitadoPor) ? (
                      <MenuItem value={envio.SolicitadoPor}>
                        {envio.SolicitadoPor}
                      </MenuItem>
                    ) : null}
                    {userNameOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                    bgcolor: alpha(theme.palette.primary.main, 0.035),
                  }}
                >
                  <EnvioDestinoFields
                    value={destinoForm}
                    onChange={applyDestinoForm}
                    disabled={requestDisabled}
                    compact
                    fieldSx={fieldSx}
                    allowedTipos={allowedDestinoTipos}
                    internoLabel={t("courierControl.destinationSdsSpain")}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.courier")}
                  value={envio.CompaniaCourier || ""}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("CompaniaCourier", e.target.value)}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {courierCompanyOptions.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.courierNumber")}
                  value={envio.NumeroAwb || ""}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("NumeroAwb", e.target.value)}
                />
              </Grid>
            </Grid>
            {shipMissing.length ? (
              <Alert
                severity="warning"
                sx={{ mt: 1.5, borderRadius: 2 }}
                onClose={() => setShipMissing([])}
              >
                {t("courierControl.confirmShipMissing", {
                  fields: shipMissing
                    .map((key) => t(`courierControl.${key}`))
                    .join(", "),
                })}
              </Alert>
            ) : null}
            {canSolicitar && !canTerminar && !shippedLocked ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 1.5,
                  px: 1.25,
                  py: 0.85,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.2),
                }}
              >
                {t("courierControl.solicitarCanEditHint")}
              </Typography>
            ) : null}
          </FormPanel>

          <FormPanel
            className="cc-finish"
            icon={<Inventory2OutlinedIcon fontSize="small" />}
            title={t("courierControl.sectionBox")}
            subtitle={t("courierControl.sectionBoxHint")}
            accent={theme.palette.success.main}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.25}
              sx={{ mb: 1.5 }}
            >
              <TextField
                select
                size="small"
                label={t("courierControl.packing")}
                value={envio.TipoEmbalaje || ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 1.5, minWidth: 0 }}
                onChange={(e) => setField("TipoEmbalaje", e.target.value)}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {envio.TipoEmbalaje &&
                !COURIER_PACKING_OPTIONS.includes(envio.TipoEmbalaje) ? (
                  <MenuItem value={envio.TipoEmbalaje}>
                    {envio.TipoEmbalaje}
                  </MenuItem>
                ) : null}
                {[...COURIER_PACKING_OPTIONS]
                  .sort((a, b) => {
                    const la = courierPackingI18nKey(a)
                      ? t(courierPackingI18nKey(a))
                      : a;
                    const lb = courierPackingI18nKey(b)
                      ? t(courierPackingI18nKey(b))
                      : b;
                    return String(la).localeCompare(String(lb), undefined, {
                      sensitivity: "base",
                    });
                  })
                  .map((key) => {
                    const i18nKey = courierPackingI18nKey(key);
                    return (
                      <MenuItem key={key} value={key}>
                        {i18nKey ? t(i18nKey) : key}
                      </MenuItem>
                    );
                  })}
              </TextField>
              <TextField
                size="small"
                label={t("courierControl.length")}
                value={envio.LargoCm ?? ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 0.7, minWidth: 72 }}
                onChange={(e) => setField("LargoCm", e.target.value)}
              />
              <TextField
                size="small"
                label={t("courierControl.width")}
                value={envio.AnchoCm ?? ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 0.7, minWidth: 72 }}
                onChange={(e) => setField("AnchoCm", e.target.value)}
              />
              <TextField
                size="small"
                label={t("courierControl.height")}
                value={envio.AltoCm ?? ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 0.7, minWidth: 72 }}
                onChange={(e) => setField("AltoCm", e.target.value)}
              />
              <TextField
                size="small"
                label={t("courierControl.grossWeight")}
                value={envio.PesoBrutoKg ?? ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 0.85, minWidth: 80 }}
                onChange={(e) => setField("PesoBrutoKg", e.target.value)}
              />
              <TextField
                size="small"
                label={t("courierControl.cost")}
                value={envio.Coste ?? ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 0.8, minWidth: 72 }}
                onChange={(e) => setField("Coste", e.target.value)}
              />
              <TextField
                size="small"
                label={t("courierControl.realCost")}
                value={envio.CosteReal ?? ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 0.9, minWidth: 80 }}
                onChange={(e) => setField("CosteReal", e.target.value)}
              />
            </Stack>
            <Stack
              direction="row"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
              sx={{
                px: 1.5,
                py: 1.2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.success.main, 0.25),
                background: `linear-gradient(90deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.06)})`,
              }}
            >
              <Chip
                size="small"
                label={`${t("courierControl.volume")}: ${fmt(measures.volumenM3, 6)} m³`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`${t("courierControl.volWeight")}: ${fmt(measures.pesoVolumetricoKg)} kg`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`${t("courierControl.chargeable")}: ${fmt(measures.pesoFacturableKg)} kg`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                color={
                  measures.diferencia != null && measures.diferencia !== 0
                    ? "warning"
                    : "default"
                }
                label={`${t("courierControl.difference")}: ${
                  measures.diferencia == null
                    ? "—"
                    : `${fmt(measures.diferencia, 2)} €`
                }`}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          </FormPanel>

          <FormPanel
            className="cc-pieces"
            icon={<WidgetsOutlinedIcon fontSize="small" />}
            title={t("courierControl.sectionPieces")}
            subtitle={t("courierControl.piecesHint")}
            accent={theme.palette.secondary?.main || theme.palette.info.main}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ mb: 1 }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={addPieceDisabled}
                title={
                  addPieceDisabled && !requestDisabled
                    ? t("courierControl.addLineLockedShipped")
                    : undefined
                }
                onClick={() =>
                  onChange({ ...envio, lineas: [...envio.lineas, emptyLine()] })
                }
                sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2 }}
              >
                {t("courierControl.addLine")}
              </Button>
            </Stack>

            {(envio.lineas || []).map((linea, index) => (
              <PieceCard
                key={
                  linea.IdCourierControlLinea ||
                  `${linea.IdMuestra || "x"}-${index}`
                }
                linea={linea}
                index={index}
                fieldSx={fieldSx}
                t={t}
                requestDisabled={requestDisabled}
                pieceFieldsDisabled={piecesLocked}
                canRemove={
                  !requestDisabled &&
                  !piecesLocked &&
                  (envio.lineas || []).length > 1
                }
                onChange={(next) => setLine(index, next)}
                onRemove={() => {
                  const label =
                    String(linea.CodigoMuestra || "").trim() ||
                    String(linea.Estilo || "").trim() ||
                    t("courierControl.piece");
                  setPendingRemove({
                    index,
                    label,
                    linked: Boolean(
                      linea.IdMovimientoMuestra || linea.IdMuestra,
                    ),
                  });
                }}
              />
            ))}

            {shippedLocked ? (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                {canTerminar
                  ? t("courierControl.statusShippedLockedHintTerminar")
                  : t("courierControl.statusShippedLockedHint")}
              </Alert>
            ) : null}
          </FormPanel>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            justifyContent: "space-between",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.96),
          }}
        >
          <Box>
            {canDelete ? (
              <Button
                color="error"
                onClick={onDelete}
                disabled={saving}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
              >
                {t("courierControl.delete")}
              </Button>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={onClose}
              disabled={saving}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {t("courierControl.cancel")}
            </Button>
            {canSave ? (
              <Button
                variant="contained"
                onClick={() => onSave()}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                  px: 2.5,
                }}
              >
                {t("courierControl.save")}
              </Button>
            ) : null}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingRemove)}
        onClose={() => setPendingRemove(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("courierControl.removePieceTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {pendingRemove?.linked
              ? t("courierControl.removePieceLinkedConfirm", {
                  label: pendingRemove.label,
                })
              : t("courierControl.removePieceConfirm", {
                  label: pendingRemove?.label || "",
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            onClick={() => setPendingRemove(null)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (!pendingRemove) return;
              onChange({
                ...envio,
                lineas: envio.lineas.filter(
                  (_, i) => i !== pendingRemove.index,
                ),
              });
              setPendingRemove(null);
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("courierControl.removePieceAction")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmShipOpen}
        onClose={() => {
          setConfirmShipOpen(false);
          setShipAck(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("courierControl.confirmShipTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t("courierControl.confirmShipLead")}
            </Typography>
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              {t("courierControl.confirmShipIrreversible")}
            </Alert>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: shipAck
                  ? alpha(theme.palette.success.main, 0.45)
                  : "divider",
                bgcolor: shipAck
                  ? alpha(theme.palette.success.main, 0.06)
                  : alpha(theme.palette.action.hover, 0.35),
              }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start", gap: 0.5 }}
                control={
                  <Checkbox
                    checked={shipAck}
                    onChange={(e) => setShipAck(e.target.checked)}
                    color="success"
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, pt: 0.35 }}
                  >
                    {t("courierControl.confirmShipAck")}
                  </Typography>
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setConfirmShipOpen(false);
              setShipAck(false);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!shipAck}
            onClick={confirmMarkShipped}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("courierControl.confirmShipAction")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeliveredOpen}
        onClose={() => {
          setConfirmDeliveredOpen(false);
          setDeliverAck(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("courierControl.confirmDeliveredTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t("courierControl.confirmDeliveredLead", {
                count: deliveredSampleLines.length || 0,
              })}
            </Typography>
            {deliveredSampleLines.length > 0 ? (
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  maxHeight: 220,
                  overflow: "auto",
                  px: 1.5,
                  py: 0.75,
                }}
              >
                {deliveredSampleLines.map((linea, idx) => (
                  <Box
                    key={
                      linea.IdCourierControlLinea ||
                      `${linea.IdMuestra || "x"}-${idx}`
                    }
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 0.55,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {linea.CodigoMuestra ||
                        linea.Estilo ||
                        (linea.IdMuestra ? `#${linea.IdMuestra}` : "—")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {linea.Motivo || ""}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("courierControl.confirmDeliveredNoSamples")}
              </Typography>
            )}
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              {t("courierControl.confirmDeliveredSamplesHint")}
            </Alert>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: deliverAck
                  ? alpha(theme.palette.success.main, 0.45)
                  : "divider",
                bgcolor: deliverAck
                  ? alpha(theme.palette.success.main, 0.06)
                  : alpha(theme.palette.action.hover, 0.35),
              }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start", gap: 0.5 }}
                control={
                  <Checkbox
                    checked={deliverAck}
                    onChange={(e) => setDeliverAck(e.target.checked)}
                    color="success"
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, pt: 0.35 }}
                  >
                    {t("courierControl.confirmDeliveredAck")}
                  </Typography>
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setConfirmDeliveredOpen(false);
              setDeliverAck(false);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!deliverAck}
            onClick={confirmMarkDelivered}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("courierControl.confirmDeliveredAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
